import { useState, useEffect } from "react";
import {
	ABN_STATUS_REGISTERED,
	abn_years,
	abn_months,
	trustee_classification,
	ABN_STATUS_UNREGISTERED,
	stateOption,
	business_abn_status,
	LOAN_PURPOSE_BUSINESS_VEHICLE,
	LOAN_PURPOSE_EQUIPMENT,
	LOAN_PURPOSE_TRUCK,
	LOAN_PURPOSE_WORKING_CAPITAL,
} from "@/lib/constants";
import { BriefcaseIcon, CarIcon, TractorIcon, TruckTrailerIcon } from "@phosphor-icons/react";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import { RadioGroupField } from "@/components/ui";
import clsx from "clsx";

export const BusinessLoanDetails = () => {
	const { formValues, setFormValue } = useFormDataStore();
	const {
		selectedIndex,
		enabledBusinessFields,
		setEnabledBusinessFields,
		enabledFields,
		setFormError,
		setValidateFormDetails,
		formContainer,
	} = useFormStateStore();

	const [isNavDivSmall, setIsNavDivSmall] = useState(false);

	// For form container
	useEffect(() => {
		const limiter = selectedIndex === 0 ? 350 : 460;

		setIsNavDivSmall(formContainer < limiter);
	}, [selectedIndex, formContainer]);

	const {
		loanPurpose,
		businessAbnStatus,
		businessName,
		assetType,
		financeReason,
		purchaseStage,
		assetAge,
		purchaseFrom,
	} = formValues;

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		loanPurpose: false,
		businessAbnStatus: false,
		businessName: false,
		assetType: false,
		financeReason: false,
		purchaseStage: false,
		assetAge: false,
		purchaseFrom: false,
	});

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormError(hasError);
	}, [errors, setFormError]);

	const handleABNStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const value = e.target.value;

		setFormValue("businessAbnStatus", value);
		// Reset form values
		setFormValue("gstStatus", "");
		setFormValue("combinedIncome", "");
		setErrors((prev) => ({ ...prev, businessAbnStatus: !value }));
	};

	// Function to validate all fields
	const validateForm = () => {
		const newErrors = {
			loanPurpose: enabledFields.loanPurpose?.length == 0 ? false : !loanPurpose,
			businessAbnStatus:
				enabledBusinessFields.businessAbnStatus?.length == 0 ? false : !businessAbnStatus,
			businessName:
				formValues.loanPurpose !== LOAN_PURPOSE_WORKING_CAPITAL ? false : !businessName,
			assetType: [LOAN_PURPOSE_BUSINESS_VEHICLE, LOAN_PURPOSE_WORKING_CAPITAL].includes(
				formValues.loanPurpose
			)
				? false
				: !assetType,
			financeReason:
				formValues.loanPurpose === LOAN_PURPOSE_WORKING_CAPITAL ||
				formValues.loanPurpose !== LOAN_PURPOSE_EQUIPMENT
					? false
					: !financeReason,
			purchaseStage:
				formValues.loanPurpose === LOAN_PURPOSE_WORKING_CAPITAL ? false : !purchaseStage,
			assetAge: formValues.loanPurpose === LOAN_PURPOSE_WORKING_CAPITAL ? false : !assetAge,
			purchaseFrom:
				formValues.loanPurpose === LOAN_PURPOSE_WORKING_CAPITAL ? false : !purchaseFrom,
		};
		setErrors(newErrors);

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormError(hasError);
		return !hasError; // Returns true if validation passes
	};

	// Store the validation function in Zustand so QuickForm can call it
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [
		loanPurpose,
		businessName,
		businessAbnStatus,
		assetType,
		financeReason,
		purchaseStage,
		assetAge,
		purchaseFrom,
	]);

	// Reset formValues where it's not enabled.
	useEffect(() => {
		if (formValues.loanPurpose) {
			setEnabledBusinessFields(formValues.loanPurpose);
		}
	}, [formValues.loanPurpose, enabledBusinessFields, setFormValue, setErrors]);

	return (
		<div className="jF-space-y-4 jF-m-2 jF-pb-4">
			<RadioGroupField
				id="loanPurpose"
				label="Loan Purpose"
				value={loanPurpose || ""}
				options={enabledFields.loanPurpose.map((item) => ({
					id: item.id,
					value: item.value,
					label: item.value,
					icon:
						item.value === LOAN_PURPOSE_BUSINESS_VEHICLE ? (
							<CarIcon size={24} weight="duotone" />
						) : item.value == LOAN_PURPOSE_EQUIPMENT ? (
							<TractorIcon size={24} weight="duotone" />
						) : item.value == LOAN_PURPOSE_TRUCK ? (
							<TruckTrailerIcon size={24} weight="duotone" />
						) : item.value == LOAN_PURPOSE_WORKING_CAPITAL ? (
							<BriefcaseIcon size={24} weight="duotone" />
						) : null,
				}))}
				onChange={(value) => {
					setFormValue("loanPurpose", value);
					// Reset form values
					setFormValue("abnStatus", "");
					setFormValue("assetType", "");
					setFormValue("businessAbnStatus", "");
					setFormValue("businessName", "");
					setFormValue("financeReason", "");
					setErrors((prevErrors) => ({
						...prevErrors,
						loanPurpose: !value,
					}));
					setEnabledBusinessFields(value);
				}}
				required={true}
				error={errors.loanPurpose ? "This field is required" : ""}
				disabled={false}
				className={""}
				gridCols={isNavDivSmall ? 2 : 4}
				showIcons={true}
				disabledOptions={[]}
				navRef={isNavDivSmall}
			/>

			{/* Business Vehicle Finance, Equipment Finance, Truck Finance */}
			{formValues.loanPurpose && enabledBusinessFields.purchaseStage?.length > 1 && (
				<>
					{/* 1. Purchase Stage */}
					<RadioGroupField
						id="purchaseStage"
						label="Have you identified the asset that you intend to acquire?"
						value={purchaseStage || ""}
						options={enabledBusinessFields.purchaseStage.map((item) => ({
							id: item.value,
							value: item.value,
							label: item.value,
							icon: null,
						}))}
						onChange={(value) => {
							setFormValue("purchaseStage", value);
							setErrors((prevErrors) => ({
								...prevErrors,
								purchaseStage: !value, // Clear error when a selection is made
							}));
						}}
						required={true}
						error={errors.purchaseStage ? "This field is required" : ""}
						disabled={false}
						className={""}
						gridCols={4}
						showIcons={true}
						disabledOptions={[]}
						navRef={isNavDivSmall}
					/>
				</>
			)}

			{formValues.loanPurpose && enabledBusinessFields.assetType?.length > 1 && (
				<>
					{/* 2. Asset Type */}
					<div className="jF-flex max-sm:jF-block max-sm:jF-space-x-0 jF-space-x-4 jF-space-y-2 sm:jF-space-y-0">
						<div className="jF-flex-1">
							<label className="jF-block jF-font-semibold jF-text-md jF-leading-6 jF-text-gray-900">
								What kind of asset do you plan to buy?
							</label>
							<div className="jF-flex jF-items-center jF-space-x-2">
								<select
									value={assetType}
									onChange={(e) => {
										setFormValue("assetType", e.target.value);
										setErrors((prevErrors) => ({
											...prevErrors,
											assetType: !e.target.value,
										}));
									}}
									id="assetType"
									aria-label="Asset Type"
									className={clsx(
										errors.assetType ? "jF-ring-red-500" : "jF-ring-gray-300",
										"jF-appearance-none jF-bg-white jF-block jF-w-full jF-rounded-md jF-shadow-sm jF-border-0 jF-py-2.5 jF-text-gray-900 jF-ring-1 jF-outline-1 jF-outline-gray-900 focus:jF-ring-1 jF-ring-inset jF-pl-3 placeholder:jF-text-gray-400 sm:jF-text-md sm:jF-leading-6"
									)}
								>
									<option value="" disabled>
										Please select...
									</option>
									{enabledBusinessFields.assetType.map((option) => (
										<option key={option.value} value={option.value}>
											{option.value}
										</option>
									))}
								</select>
							</div>
							{errors.assetType && (
								<span className="jF-text-red-500 jF-text-sm">
									This field is required
								</span>
							)}
						</div>
					</div>
				</>
			)}

			{formValues.loanPurpose && enabledBusinessFields.financeReason?.length > 1 && (
				<>
					{/* 3. Finance Reason - Equipment Finance*/}
					<div className="jF-flex-1">
						<label className="jF-text-base jF-font-semibold jF-leading-6 jF-text-gray-900">
							What is the primary reason for seeking equipment finance?
						</label>
						<div className="jF-flex jF-items-center jF-space-x-2">
							<select
								value={financeReason}
								onChange={(e) => {
									setFormValue("financeReason", e.target.value);
									setErrors((prevErrors) => ({
										...prevErrors,
										financeReason: !e.target.value,
									}));
								}}
								id="financeReason"
								aria-label="Primary reason for seeking equipment finance"
								className={clsx(
									errors.financeReason
										? "jF-ring-red-500 focus:jF-ring-red-500"
										: "jF-ring-gray-300 focus:jF-ring-gray-900",
									"jF-appearance-none jF-bg-white jF-w-full jF-block jF-rounded-md jF-border-0 jF-py-2.5 jF-px-4 jF-text-gray-900 jF-ring-1 jF-ring-inset jF-ring-gray-300 focus:jF-ring-1 jF-outline-1 jF-outline-gray-900 focus:jF-ring-inset sm:jF-text-md sm:jF-leading-6"
								)}
							>
								<option value="" disabled>
									Please select...
								</option>
								{enabledBusinessFields.financeReason.map((option) => (
									<option key={option.value} value={option.value}>
										{option.value}
									</option>
								))}
							</select>
						</div>
						{errors.financeReason && (
							<span className="jF-text-red-500 jF-text-sm">
								This field is required
							</span>
						)}
					</div>
				</>
			)}

			{formValues.loanPurpose && enabledBusinessFields.assetAge?.length > 1 && (
				<>
					{/* 3. Asset Age */}
					<RadioGroupField
						id="assetAge"
						label="In what state or condition is the asset?"
						value={assetAge || ""}
						options={enabledBusinessFields.assetAge.map((item) => ({
							id: item.value,
							value: item.value,
							label: item.value,
							icon: null,
						}))}
						onChange={(value) => {
							setFormValue("assetAge", value);
							setErrors((prevErrors) => ({
								...prevErrors,
								assetAge: !value, // Clear error when a selection is made
							}));
						}}
						required={true}
						error={errors.assetAge ? "This field is required" : ""}
						disabled={false}
						className={""}
						gridCols={4}
						showIcons={true}
						disabledOptions={[]}
						navRef={isNavDivSmall}
					/>
				</>
			)}

			{formValues.loanPurpose && enabledBusinessFields.purchaseFrom?.length > 1 && (
				<>
					{/* 4. Purchase From */}
					<RadioGroupField
						id="purchaseFrom"
						label="Which seller type would you prefer?"
						value={purchaseFrom || ""}
						options={enabledBusinessFields.purchaseFrom.map((item) => ({
							id: item.value,
							value: item.value,
							label: item.value,
							icon: null,
						}))}
						onChange={(value) => {
							setFormValue("purchaseFrom", value);
							setErrors((prevErrors) => ({
								...prevErrors,
								purchaseFrom: !value, // Clear error when a selection is made
							}));
						}}
						required={true}
						error={errors.purchaseFrom ? "This field is required" : ""}
						disabled={false}
						className={""}
						gridCols={4}
						showIcons={true}
						disabledOptions={[]}
						navRef={isNavDivSmall}
					/>
				</>
			)}

			{/* Working Capital */}
			{formValues.loanPurpose === LOAN_PURPOSE_WORKING_CAPITAL &&
				enabledBusinessFields.businessAbnStatus?.length > 1 && (
					<>
						{/* 1. ABN Status */}
						<div className="jF-grid jF-grid-cols-1">
							<label className="jF-block jF-text-md jF-font-medium jF-leading-6 jF-text-gray-900">
								What is your current ABN status?
							</label>
							<select
								value={businessAbnStatus}
								onChange={handleABNStatusChange}
								id="businessAbnStatus"
								aria-label="Business ABN Status"
								className={clsx(
									errors.businessAbnStatus
										? "jF-ring-red-500 focus:jF-ring-red-500"
										: "jF-ring-gray-300 focus:jF-ring-gray-900",
									"jF-appearance-none jF-bg-white jF-mt-2 jF-block jF-w-full jF-rounded-md jF-border-0 jF-py-2.5 jF-pl-3 jF-pr-10 jF-text-gray-900 jF-ring-1 jF-ring-inset jF-ring-gray-300 focus:jF-ring-1 jF-outline-1 jF-outline-gray-900 focus:jF-ring-inset sm:jF-text-md sm:jF-leading-6"
								)}
							>
								{enabledBusinessFields.businessAbnStatus.map((option) => (
									<option key={option.value} value={option.value}>
										{option.option}
									</option>
								))}
							</select>
							{errors.businessAbnStatus && (
								<span className="jF-text-red-500 jF-text-sm">
									This field is required
								</span>
							)}
						</div>
					</>
				)}

			{formValues.loanPurpose === LOAN_PURPOSE_WORKING_CAPITAL && (
				<>
					{/* 2. Business Name */}
					<div>
						<label className="jF-block jF-text-md jF-font-medium jF-leading-6 jF-text-gray-900">
							What is the name associated with your venture?
						</label>
						<input
							value={businessName}
							onChange={(e) => {
								setFormValue("businessName", e.target.value);
								setErrors((prevErrors) => ({
									...prevErrors,
									businessName: !e.target.value,
								}));
							}}
							id="businessName"
							aria-label="business Name"
							className={clsx(
								errors.businessName
									? "jF-ring-red-500 focus:jF-ring-red-500"
									: "jF-ring-gray-300 focus:jF-ring-gray-900",
								"jF-appearance-none jF-bg-white jF-mt-2 jF-block jF-w-full jF-rounded-md jF-border-0 jF-py-2.5 jF-pl-3 jF-pr-10 jF-text-gray-900 jF-ring-1 jF-ring-inset jF-ring-gray-300 focus:jF-ring-1 jF-outline-1 jF-outline-gray-900 focus:jF-ring-inset sm:jF-text-md sm:jF-leading-6"
							)}
						/>
						{errors.businessName && (
							<span className="jF-text-red-500 jF-text-sm">
								This field is required
							</span>
						)}
					</div>
				</>
			)}
		</div>
	);
};
